import { Link, useNavigate, useParams } from "react-router-dom";
import { LazyLoadImage } from "react-lazy-load-image-component";
import ButtonLink from "../components/buttons/ButtonLink.tsx";
import { ButtonVariant } from "../types/button.ts";
import { useEffect, useState } from "react";
import { useAuth } from "../hooks/useAuth.tsx";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/solid";
import { UnauthorizedMessageStatusTypes } from "../exceptions/unauthorizedException.ts";
import UtilsHelper from "../helpers/utilsHelper.ts";

const ConfirmRegistrationPage = () => {
    const { token } = useParams();
    const _authService = useAuth();
    const _navigate = useNavigate();
    const [isSuccess, setIsSuccess] = useState<undefined | true | false>(undefined);
    const [errorType, setErrorType] = useState<UnauthorizedMessageStatusTypes | null | undefined>(null);
    useEffect(() => {
        UtilsHelper.changeWindowDocumentTitle("Potwierdzenie rejestracji");
        (async () => {
            if (token) {
                try {
                    await _authService.confirmRegistration(token);
                    setIsSuccess(true);
                } catch (e) {
                    const error = e as { code: number, status: UnauthorizedMessageStatusTypes };
                    if (error.code === 401) {
                        setErrorType(error.status);
                    }
                    if (error.code === 500)
                        _navigate("/");
                    setIsSuccess(false);
                }
            }
        })();
    }, [token]);
    return (
        <div className={"flex flex-col min-h-screen justify-between items-center text-neutral-100 px-2 text-center"}>
            <Link to={"/"}>
                <LazyLoadImage
                    alt=""
                    className="object-cover auto min-h-full h-16 md:h-14 my-4"
                    effect="opacity"
                    src={`/images/logofull.png`}
                />
            </Link>
            {isSuccess === undefined &&
                <section className={"self-center my-5 max-w-xs"}>
                    <svg
                        className={"mx-auto mt-16"}
                        width="44"
                        height="44"
                        viewBox="0 0 44 44"
                        xmlns="http://www.w3.org/2000/svg"
                        stroke="#fff">
                        <g fill="none" fillRule="evenodd" strokeWidth="2">
                            <circle cx="22" cy="22" r="1">
                                <animate
                                    attributeName="r"
                                    begin="0s"
                                    dur="1.8s"
                                    values="1; 20"
                                    calcMode="spline"
                                    keyTimes="0; 1"
                                    keySplines="0.165, 0.84, 0.44, 1"
                                    repeatCount="indefinite" />
                                <animate
                                    attributeName="stroke-opacity"
                                    begin="0s"
                                    dur="1.8s"
                                    values="1; 0"
                                    calcMode="spline"
                                    keyTimes="0; 1"
                                    keySplines="0.3, 0.61, 0.355, 1"
                                    repeatCount="indefinite" />
                            </circle>
                            <circle cx="22" cy="22" r="1">
                                <animate
                                    attributeName="r"
                                    begin="-0.9s"
                                    dur="1.8s"
                                    values="1; 20"
                                    calcMode="spline"
                                    keyTimes="0; 1"
                                    keySplines="0.165, 0.84, 0.44, 1"
                                    repeatCount="indefinite" />
                                <animate
                                    attributeName="stroke-opacity"
                                    begin="-0.9s"
                                    dur="1.8s"
                                    values="1; 0"
                                    calcMode="spline"
                                    keyTimes="0; 1"
                                    keySplines="0.3, 0.61, 0.355, 1"
                                    repeatCount="indefinite" />
                            </circle>
                        </g>
                    </svg>
                </section>
            }
            {isSuccess &&
                <section className={"self-center my-5 max-w-lg animate-opacityEaseIn bg-neutral-900 rounded-3xl py-16 px-12 shadow-3xl"}>
                    <CheckCircleIcon className={"w-16 h-16 text-green-400 mx-auto mb-4 rounded-full"} />
                    <h1 className={"text-3xl mb-4"}>Twoje konto zostało aktywowane</h1>
                    <p className={"text-neutral-400 mb-5"}>Twoje konto zostało pomyślnie aktywowane. Teraz możesz
                        przejść
                        na <ButtonLink to={"/"} variant={ButtonVariant.SECONDARY}
                            className={"pl-0 pr-0 pt-0 pb-0"}>stronę główną
                        </ButtonLink> i się zalogować.</p>
                </section>
            }
            {!isSuccess && errorType === undefined &&
                <section className={"self-center my-5 max-w-lg animate-opacityEaseIn bg-neutral-900 rounded-3xl py-16 px-12 shadow-3xl"}>
                    <XCircleIcon className={"w-16 h-16 text-red-400 mx-auto mb-4 rounded-full"} />
                    <h1 className={"text-3xl mb-4"}>Nieprawidłowy link aktywacyjny</h1>
                    <p className={"text-neutral-400"}>Link aktywacyjny jest nieprawidłowy. Proszę zweryfikować
                        poprawność
                        linku.</p>
                </section>
            }
            {!isSuccess && errorType === "INVALID_TOKEN_SENT_NEW_EMAIL" &&
                <section className={"self-center my-5 max-w-lg animate-opacityEaseIn bg-neutral-900 rounded-3xl py-16 px-12 shadow-3xl"}>
                    <XCircleIcon className={"w-16 h-16 text-red-400 mx-auto mb-4 rounded-full"} />
                    <h1 className={"text-3xl mb-4"}>Link aktywacyjny wygasł</h1>
                    <p className={"text-neutral-400"}>Link aktywacyjny wygasł. Wysłaliśmy email z nowym linkiem
                        aktywacyjnym. Proszę sprawdzić pocztę i ponownie kliknąć w nowy link aktywacyjny. (Mail nie
                        dotarł? Sprawdź folder SPAM)</p>
                </section>
            }
            {!isSuccess && errorType === "INVALID_TOKEN" &&
                <section className={"self-center my-5 max-w-lg animate-opacityEaseIn bg-neutral-900 rounded-3xl py-16 px-12 shadow-3xl"}>
                    <XCircleIcon className={"w-16 h-16 text-red-400 mx-auto mb-4 rounded-full"} />
                    <h1 className={"text-3xl mb-4"}>Link aktywacyjny wygasł</h1>
                    <p className={"text-neutral-400"}>Link aktywacyjny wygasł. Na twojej skrzynce powinna znajdować się
                        nowsza wiadomość z linkiem aktywacyjnym. (Nie możesz znaleźć wiadomości? Sprawdź folder
                        SPAM)</p>
                </section>
            }
            <div className={"min-h-12"} />
        </div>
    );
};

export default ConfirmRegistrationPage;